import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Eye, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  apiErrorMessage,
  downloadDocument,
  useApiDocsManager,
  useDeleteDocument,
  useDocuments,
  useRequireApiDocsManager,
} from '../api';
import { documentTypeLabel, formatFileSize, type AppDocument } from '../types';

export function DocumentListPage() {
  useRequireApiDocsManager();
  const { canModify } = useApiDocsManager();
  const docs = useDocuments();
  const remove = useDeleteDocument();
  const [toDelete, setToDelete] = useState<AppDocument | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = async (d: AppDocument) => {
    setDownloadingId(d.id);
    try {
      await downloadDocument(d.id, d.originalFileName);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Download failed'));
    } finally {
      setDownloadingId(null);
    }
  };

  const confirmDelete = () => {
    if (!toDelete) return;
    remove.mutate(toDelete.id, {
      onSuccess: (r) => {
        toast.success(r.message);
        setToDelete(null);
      },
      onError: (e) => toast.error(apiErrorMessage(e, 'Could not delete the document')),
    });
  };

  const columns = useMemo<ColumnDef<AppDocument>[]>(
    () => [
      {
        id: 'index',
        header: 'Sr No',
        size: 60,
        enableSorting: false,
        cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.index + 1}</span>,
      },
      { accessorKey: 'documentName', header: 'Document' },
      {
        accessorKey: 'documentType',
        header: 'Type',
        cell: ({ row }) => documentTypeLabel(row.original.documentType),
      },
      { accessorKey: 'originalFileName', header: 'File' },
      { accessorKey: 'fileSize', header: 'Size', cell: ({ row }) => formatFileSize(row.original.fileSize) },
      { accessorKey: 'uploadedByName', header: 'Uploaded By', cell: ({ row }) => row.original.uploadedByName ?? '—' },
      { accessorKey: 'createdAt', header: 'Uploaded On' },
      {
        id: 'actions',
        header: 'Action',
        size: 130,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" asChild>
              <Link to={`/api-docs/document/${row.original.id}`} aria-label="View document">
                <Eye className="h-3.5 w-3.5" />
              </Link>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={downloadingId !== null}
              onClick={() => void handleDownload(row.original)}
              aria-label="Download"
            >
              {downloadingId === row.original.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
            </Button>
            {canModify(row.original.uploadedBy) && (
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setToDelete(row.original)}
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canModify, downloadingId],
  );

  return (
    <FillPage>
      <PageHeader
        title="Upload Document"
        description="Documents shared with your users."
        actions={
          <Button asChild>
            <Link to="/superadmin-api-docs/upload-document/add">
              <Plus className="h-4 w-4" />
              Upload Document
            </Link>
          </Button>
        }
      />

      <DataTable<AppDocument, unknown>
        tableId="api-docs:documents:v2"
        columns={columns}
        data={docs.data ?? []}
        isLoading={docs.isLoading}
        searchPlaceholder="Search documents…"
        emptyTitle="No documents yet"
        emptyDescription="Upload a document to share it."
        getRowId={(row) => String(row.id)}
        pageSize={25}
        disableExport
      />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Are you sure you want to delete?"
        description={<>Document <strong>{toDelete?.documentName}</strong> will be removed for everyone.</>}
        confirmLabel="Delete"
        tone="destructive"
        loading={remove.isPending}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
}
